import { t } from "@lingui/macro"
import { useCallback, useMemo } from "react"
import { Tooltip } from "@mui/material"

import { GridRenderCellParams } from "@mui/x-data-grid-pro"

import { AccessTypeHeader } from "src/components/AccessTypeHeader"
import { RowCellContainer } from "src/components/RowCellContainer"
import { IColumn, IGenericSingleSelectOptions } from "src/types/data-grid-pro"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"
import { KNOWLEDGE_TYPES } from "src/shared/knowledge-base/constants"

export const useKnowledgeBaseListColumns = (
    agentAccessTypes: IGenericSingleSelectOptions[],
): IColumn<IKnowledgeItem>[] => {
    const renderTextCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>, fallback = "-") => {
            const value = Array.isArray(params.value)
                ? params.value.join(", ")
                : params.value
            return <RowCellContainer>{value ?? fallback}</RowCellContainer>
        },
        [],
    )

    const renderAccessTypeCell = useCallback(
        (params: GridRenderCellParams) => (
            <AccessTypeHeader accessType={params.value} />
        ),
        [],
    )

    const renderAgentTypesCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const agentTypeIds = params.row.agent_types ?? []

            if (agentTypeIds.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            // Get agent type names from IDs
            const agentTypeNames = agentTypeIds
                .map(
                    (id) =>
                        agentAccessTypes.find((type) => type.value === id)
                            ?.label,
                )
                .filter(Boolean) as string[]

            if (agentTypeNames.length <= 2) {
                // Show names when 2 or fewer
                return (
                    <RowCellContainer>
                        {agentTypeNames.join(", ")}
                    </RowCellContainer>
                )
            }

            // Show count with tooltip when more than 2
            return (
                <Tooltip title={agentTypeNames.join(", ")} arrow>
                    <RowCellContainer>
                        {`(${
                            agentTypeNames.length
                        }) ${t`knowledge-base-list-view.agent-types`.toLowerCase()}`}
                    </RowCellContainer>
                </Tooltip>
            )
        },
        [agentAccessTypes],
    )

    const renderAgentsCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const agentIds = params.row.agents ?? []

            if (agentIds.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            // For specific agent instances, we just show the count since we don't have names
            // This represents specifically connected agent instances
            return (
                <RowCellContainer>
                    {agentIds.length <= 2
                        ? `${agentIds.length} ${
                              agentIds.length === 1
                                  ? t`knowledge-base-list-view.agent-instance`
                                  : t`knowledge-base-list-view.agent-instances`
                          }`
                        : `(${
                              agentIds.length
                          }) ${t`knowledge-base-list-view.agent-instances`.toLowerCase()}`}
                </RowCellContainer>
            )
        },
        [],
    )

    const renderSegmentsCell = useCallback(
        (params: GridRenderCellParams<IKnowledgeItem>) => {
            const segmentNames = params.row.segment_names ?? []

            if (segmentNames.length === 0) {
                return <RowCellContainer>-</RowCellContainer>
            }

            if (segmentNames.length <= 2) {
                // Show names when 2 or fewer
                return (
                    <RowCellContainer>
                        {segmentNames.join(", ")}
                    </RowCellContainer>
                )
            }

            // Show count with tooltip when more than 2
            return (
                <Tooltip title={segmentNames.join(", ")} arrow>
                    <RowCellContainer>
                        {`(${
                            segmentNames.length
                        }) ${t`knowledge-base-list-view.segments`.toLowerCase()}`}
                    </RowCellContainer>
                </Tooltip>
            )
        },
        [],
    )

    return useMemo(() => {
        const columns: IColumn<IKnowledgeItem>[] = [
            {
                field: "id",
                headerName: t`knowledge-base-list-view.columns.id`,
                renderCell: renderTextCell,
                type: "number",
                minWidth: 150,
                flex: 0.5,
                filterable: false,
                sortable: false,
            },
            {
                field: "title",
                headerName: t`knowledge-base-list-view.columns.title`,
                renderCell: renderTextCell,
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                field: "knowledge_item_type",
                headerName: t`knowledge-base-list-view.columns.knowledge-item-type`,
                renderCell: renderTextCell,
                type: "singleSelect",
                minWidth: 130,
                flex: 1,
                // @ts-ignore
                // Value options is available on one of the options for type of TColumn(GridSingleSelectColDef), but it's not reading here
                valueOptions: KNOWLEDGE_TYPES,
            },
            {
                field: "agent_types",
                headerName: t`knowledge-base-list-view.columns.agent-types`,
                renderCell: renderAgentTypesCell,
                type: "singleSelect",
                minWidth: 180,
                flex: 1,
                // @ts-ignore
                // Value options is available on one of the options for type of TColumn(GridSingleSelectColDef), but it's not reading here
                valueOptions: agentAccessTypes,
            },
            {
                field: "agents",
                headerName: t`knowledge-base-list-view.columns.agents`,
                renderCell: renderAgentsCell,
                type: "string",
                minWidth: 180,
                flex: 1,
                sortable: false,
                filterable: false,
            },
            {
                field: "segment_names",
                headerName: t`knowledge-base-list-view.columns.segments`,
                renderCell: renderSegmentsCell,
                type: "string",
                minWidth: 180,
                flex: 1,
                sortable: false,
                filterable: false,
            },
            {
                field: "access_type",
                headerName: t`knowledge-base-list-view.columns.access-type`,
                renderCell: renderAccessTypeCell,
                type: "string",
                minWidth: 130,
                flex: 0.8,
                sortable: false,
                filterable: false,
            },
        ]

        return columns
    }, [
        renderAccessTypeCell,
        renderAgentTypesCell,
        renderAgentsCell,
        renderSegmentsCell,
        renderTextCell,
        agentAccessTypes,
    ])
}
